import { type FC } from "react";
import { AppBar, Button, Divider, Toolbar, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import { useTranslation } from "react-i18next";
import { useDeleteLinksMutation } from "@/partner/entities/links";

interface LinksListToolbarProps {
  selectedIds: string[];
  onCloseSelected: () => void;
}

/** Selection toolbar above the links table (as in PromocodesList). */
export const LinksListToolbar: FC<LinksListToolbarProps> = ({
  selectedIds,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const numSelected = selectedIds.length;

  const [deleteLinks, { loading: deleteLoading }] = useDeleteLinksMutation({
    refetchQueries: ["LinksList"],
  });

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deleteLinks({
          variables: { ids: selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("links.toolbar.deleteSuccess"), {
              variant: "success",
            });
          },
          onError: (error: { message: string }) => {
            enqueueSnackbar(error.message, { variant: "error" });
          },
        }),
    });
  };

  return (
    <AppBar position="static" color="default" elevation={0}>
      <Toolbar sx={{ position: "relative" }}>
        <Typography sx={{ mr: 2 }}>
          {t("common.toolbar.selectedCount", { count: numSelected })}
        </Typography>
        <Divider
          orientation="vertical"
          variant="middle"
          sx={{ height: "2em" }}
        />
        <Button
          sx={{ ml: 2 }}
          color="inherit"
          onClick={onDeleteSelected}
          disabled={deleteLoading || numSelected === 0}
        >
          {t("common.actions.delete")}
        </Button>
        <ToolbarProgress loading={deleteLoading} />
        {confirmDialog}
      </Toolbar>
    </AppBar>
  );
};
